import { useDrawdownRepaymentSchedule } from '@api/lendingAPI/lapiHooks';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getIsInterestOnlyOffer } from '@helpers/FormalOffers/getIsInterestOnlyOffer';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { ErrorMessage, Text } from '@iwoca/orion-ui';
import { Skeleton } from '@ui';
import { FormattedMessage } from 'react-intl';

import { isSelectedAmountValid } from '../../../../../ui/ChooseAmountInputWithSlider/isSelectedAmountValid';

export const RepaymentSummary = ({
    drawdownAmount,
    selectedOffer
}: {
    drawdownAmount: number;
    selectedOffer: TFormalOffer | null;
}) => {
    if (!selectedOffer) {
        return (
            <div className="flex items-center justify-between">
                <Text>
                    <FormattedMessage
                        id="_.topup_choice.min_monthly_repayment"
                        defaultMessage="Min. monthly repayment"
                    />
                </Text>
                <Text className="font-med" data-testid="min-monthly-repayment">
                    <FormattedMessage
                        id="_.topup_choice.min_monthly_repayment_placeholder"
                        defaultMessage="£ --.--"
                    />
                </Text>
            </div>
        );
    }

    if (getIsInterestOnlyOffer(selectedOffer)) {
        return (
            <InterestOnlyDrawdownRepaymentSummary
                selectedOffer={selectedOffer}
                drawdownAmount={drawdownAmount}
            />
        );
    }

    return (
        <DrawdownRepaymentSummary
            selectedOffer={selectedOffer}
            drawdownAmount={drawdownAmount}
        />
    );
};

const DrawdownRepaymentSummary = ({
    selectedOffer,
    drawdownAmount
}: {
    selectedOffer: TFormalOffer;
    drawdownAmount: number;
}) => {
    const { data, isLoading, isError } = useDrawdownRepaymentSchedule({
        offerId: selectedOffer.offer_id!,
        drawdownAmount,
        enabled: isSelectedAmountValid(drawdownAmount, selectedOffer)
    });

    if (isError)
        return (
            <ErrorMessage>
                <ErrorMessage.Icon />
                <ErrorMessage.Text>
                    <FormattedMessage
                        id="_.topup_choice.generic_error_message"
                        defaultMessage="Oops, it looks like something went wrong"
                    />
                </ErrorMessage.Text>
            </ErrorMessage>
        );

    return (
        <div className="flex items-center justify-between">
            <Text>
                <FormattedMessage
                    id="_.topup_choice.min_monthly_repayment"
                    defaultMessage="Min. monthly repayment"
                />
            </Text>
            {isLoading ? (
                <Skeleton
                    className="h-[10px] w-[75px]"
                    data-testid="min-monthly-repayment-skeleton"
                />
            ) : (
                <Text className="font-med" data-testid="min-monthly-repayment">
                    <FormattedCurrency
                        value={
                            data!.summary.simplified_schedule!.equal_repayments!
                                .amount
                        }
                    />
                </Text>
            )}
        </div>
    );
};

const InterestOnlyDrawdownRepaymentSummary = ({
    selectedOffer,
    drawdownAmount
}: {
    selectedOffer: TFormalOffer;
    drawdownAmount: number;
}) => {
    const { data, isLoading, isError } = useDrawdownRepaymentSchedule({
        offerId: selectedOffer.offer_id!,
        drawdownAmount,
        enabled: isSelectedAmountValid(drawdownAmount, selectedOffer)
    });

    if (isError)
        return (
            <ErrorMessage>
                <ErrorMessage.Icon />
                <ErrorMessage.Text>
                    <FormattedMessage
                        id="_.topup_choice.generic_error_message"
                        defaultMessage="Oops, it looks like something went wrong"
                    />
                </ErrorMessage.Text>
            </ErrorMessage>
        );

    const interestOnlyPeriodMonths = selectedOffer.interest_only_repayments!;
    const monthAfterInterestOnlyPeriod = interestOnlyPeriodMonths + 1;

    return (
        <>
            <div className="flex items-start justify-between">
                <div>
                    <Text>
                        <FormattedMessage
                            id="_.topup_choice.min_monthly_repayment"
                            defaultMessage="Min. monthly repayment"
                        />
                    </Text>
                    <Text className="text-s">
                        <FormattedMessage
                            id="_.topup_choice.min_monthly_repayment_interest_only_period"
                            defaultMessage="(from today for {interestOnlyPeriodMonths} months)"
                            values={{ interestOnlyPeriodMonths }}
                        />
                    </Text>
                </div>
                {isLoading ? (
                    <Skeleton
                        className="h-[10px] w-[75px]"
                        data-testid="interest-only-repayments-skeleton"
                    />
                ) : (
                    <Text
                        className="font-med"
                        data-testid="interest-only-repayments"
                    >
                        <FormattedCurrency value={data!.schedule[0].total} />
                    </Text>
                )}
            </div>
            <div className="flex items-start justify-between">
                <div>
                    <Text>
                        <FormattedMessage
                            id="_.topup_choice.min_monthly_repayment"
                            defaultMessage="Min. monthly repayment"
                        />
                    </Text>
                    <Text className="text-s">
                        <FormattedMessage
                            id="_.topup_choice.min_monthly_repayment_after_interest_only_period"
                            defaultMessage="({monthAfterInterestOnlyPeriod}th month until term end)"
                            values={{ monthAfterInterestOnlyPeriod }}
                        />
                    </Text>
                </div>
                {isLoading ? (
                    <Skeleton
                        className="h-[10px] w-[75px]"
                        data-testid="min-monthly-repayment-after-interest-only-skeleton"
                    />
                ) : (
                    <Text
                        className="font-med"
                        data-testid="min-monthly-repayment-after-interest-only"
                    >
                        <FormattedCurrency
                            value={
                                data!.schedule[interestOnlyPeriodMonths].total
                            }
                        />
                    </Text>
                )}
            </div>
        </>
    );
};
